import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  type WebRemoteConfigKey,
  REMOTE_CONFIG_DEFAULTS_BY_APP,
} from "@graftus/remote-config-params";
import { parseRemoteConfigBoolean } from "@/lib/parseRemoteConfigBoolean";
import {
  getRemoteConfigBoolean,
  getRemoteConfigNumber,
  getRemoteConfigString,
  initializeRemoteConfig,
} from "@/lib/remoteConfig";

type RemoteConfigContextValue = {
  /** True after the first initializeRemoteConfig() attempt finishes (success or failure). */
  ready: boolean;
  /** Increments when config may have changed; use to re-read getters. */
  generation: number;
};

const RemoteConfigContext = createContext<RemoteConfigContextValue | null>(
  null,
);

function useRemoteConfigContext(): RemoteConfigContextValue {
  const ctx = useContext(RemoteConfigContext);
  if (!ctx) {
    throw new Error("useRemoteConfig* must be used within RemoteConfigProvider");
  }
  return ctx;
}

export function RemoteConfigProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void initializeRemoteConfig().finally(() => {
      if (!cancelled) {
        setReady(true);
        setGeneration((g) => g + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !import.meta.env.DEV) return;
    if (getRemoteConfigBoolean("use_discover_api")) {
      console.log("[Remote Config] use_discover_api is enabled");
    }
  }, [ready]);

  const value = useMemo(
    () => ({ ready, generation }),
    [ready, generation],
  );

  return (
    <RemoteConfigContext.Provider value={value}>
      {children}
    </RemoteConfigContext.Provider>
  );
}

export function useRemoteConfigReady(): boolean {
  return useRemoteConfigContext().ready;
}

export function useRemoteConfigBoolean(key: WebRemoteConfigKey): boolean {
  const { generation } = useRemoteConfigContext();
  return useMemo(
    () => getRemoteConfigBoolean(key),
    [key, generation],
  );
}

export function useRemoteConfigString(key: WebRemoteConfigKey): string {
  const { generation } = useRemoteConfigContext();
  return useMemo(
    () => getRemoteConfigString(key),
    [key, generation],
  );
}

export function useRemoteConfigNumber(key: WebRemoteConfigKey): number {
  const { generation } = useRemoteConfigContext();
  return useMemo(
    () => getRemoteConfigNumber(key),
    [key, generation],
  );
}

/**
 * Boolean flag before Remote Config has finished its first fetch.
 * Matches the web defaults for the key (typically false for "0").
 */
export function getRemoteConfigBooleanDefault(key: WebRemoteConfigKey): boolean {
  return parseRemoteConfigBoolean(REMOTE_CONFIG_DEFAULTS_BY_APP.web[key]);
}
